import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { withStyles } from 'tss-react/mui';
import TextFieldFormik from 'src/components/TextFieldFormik';
import AttachmentField from 'src/components/AttachmentField';
import useDefinitionAttachments, { definitionExtrasPayload } from 'src/hooks/useDefinitionAttachments';
import { ATTACHMENTS_BUSY_MESSAGE } from 'src/helpers/attachmentBudget';
import {
  SCREEN,
  NOTIFICATION_STATUS,
  STORAGE,
  MESSAGES,
} from 'src/constants';
import * as Yup from 'yup';
import {
  setFileUploadToMessage,
  createMessage,
  updateMessage,
} from 'src/actions/messagesActions';
import { addNotification } from 'src/actions/notificationActions';
import { useSnackbar } from 'notistack';
import TextFieldEmojiMaxLength from 'src/views/Settings/MessageView/EditMessage/TextFieldEmojiMaxLength';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import { SF_ENTITY_LABELS } from 'src/constants/smartFieldLabels';
import {
  TEMPLATE_CONTEXT_SCOPE,
  getTemplateContextScopeConfig,
  findLegacyTokenScopes,
} from 'src/constants/templateContextScope';
import helpers from 'src/helpers';

import editMessageStyle from 'src/views/Settings/MessageView/EditMessage/editMessage.style';

const { VCARD_FILENAME } = MESSAGES;

const MAX_TEXT_MESSAGE_LENGTH = 1024;
const MIN_TEXT_MESSAGE_LENGTH = 2;

const VALIDATE_MESSAGE = {
  validateFileSize: 'File size must be less than 300Kb',
  validateFileType: 'Must be a jpg, jpeg, png, or gif file',
};

function EditMessage({
  message,
  classes,
  loading,
  userMessageCodes,
}) {
  const {
    id,
    code,
    description,
    systemGenMessage,
    attachment,
    attachmentContainer,
    additionalAttachments,
    fileUpload = false,
    addNewTemplate = false,
  } = message || {};

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    user, azAccountName, databaseBeingUsed, mmsAttachmentPolicy,
  } = useSelector((state) => state.account);
  // CON-1936 — true while a file is being added (multi-attachment beta).
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);

  const [windowWidth, setWindowWidth] = useState(0);

  const isItUserTemplate = helpers.isItUserMassageTemplate(code) || addNewTemplate;

  const getValuesFromCode = (value, msg = false) => {
    if (msg) {
      return value ? value.replace(/\d/g, '') : MESSAGES.MESSAGE_USER_CODE_TYPE;
    }
    return value ? value.replace(/\D/g, '') : '';
  };

  const getNextCodeValue = () => {
    if (addNewTemplate) {
      const newUserMessageCodes = helpers.transform.toArray(userMessageCodes);
      for (let i = 1; i <= 999; i++) {
        const newCode = helpers.transform.toMessageCode(MESSAGES.MESSAGE_USER_CODE_TYPE, i);
        if (!newUserMessageCodes.find((item) => item === newCode)) {
          return newCode;
        }
      }
    }

    return '';
  };

  const hasCodeAlreadyBeenUsed = (value) => {
    const newCode = helpers.transform.toMessageCode(MESSAGES.MESSAGE_USER_CODE_TYPE, value);
    if (newCode === code) {
      return true;
    }

    return !helpers.transform.toArray(userMessageCodes).find((item) => item === newCode);
  };

  useEffect(() => {
    const updateWindowWidth = () => {
      setWindowWidth(window.innerWidth);
    };
    updateWindowWidth();
    window.addEventListener('resize', updateWindowWidth);
    return () => window.removeEventListener('resize', updateWindowWidth);
  }, []);

  const defaultScreenSpacing = windowWidth < SCREEN.MD ? 2 : 5;

  const initialValues = {
    message: isItUserTemplate ? getValuesFromCode(code, true) : code || '',
    message2: getValuesFromCode(isItUserTemplate && !addNewTemplate ? code : getNextCodeValue()),
    scenario: description || '',
    systemGenMessage: systemGenMessage || '',
    attachment: attachment || '',
    attachmentContainer: attachmentContainer || null,
    // CON-1936 — files 2..N (multi-attachment beta).
    additionalAttachments: Array.isArray(additionalAttachments) ? additionalAttachments : [],
  };

  const validationSchema = Yup.object().shape({
    ...(isItUserTemplate ? {
      message2: Yup.number()
        .moreThan(0, 'Must be greater than 0')
        .lessThan(1000, 'Must be less than 1000')
        .test('hasCodeAlreadyBeenUsed', 'This template number has already been used.', hasCodeAlreadyBeenUsed)
        .required('This is required field'),
      scenario: Yup.string().required('This is required field'),
    } : {}),
    systemGenMessage: Yup.string()
      .min(MIN_TEXT_MESSAGE_LENGTH, `Text Message to Send must be at least ${MIN_TEXT_MESSAGE_LENGTH} characters`)
      .max(MAX_TEXT_MESSAGE_LENGTH, `Text Message to Send must be at most ${MAX_TEXT_MESSAGE_LENGTH} characters`),
    attachment: Yup.string()
      .nullable()
      .test('invalidAttachmentSize', VALIDATE_MESSAGE.validateFileSize, () => helpers.azure.validateFileSizeForForm(fileUpload))
      .test('invalidAttachmentType', VALIDATE_MESSAGE.validateFileType, () => helpers.azure.validateFileType(fileUpload)),
  });

  const fileWasNotUpload = () => {
    dispatch(addNotification('file upload', 'Your file wasn\'t uploaded', NOTIFICATION_STATUS.ERROR));
  };

  const fileWasNotDelete = () => {
    dispatch(addNotification('file delete', 'Your file wasn\'t deleted', NOTIFICATION_STATUS.ERROR));
  };

  const onSubmit = async (values, {
    resetForm, setErrors, setStatus, setSubmitting
  }) => {
    const doWhenTry = () => {
      dispatch(setFileUploadToMessage(false));
      resetForm();
      setStatus({ success: true });
      setSubmitting(false);
    };
    const doWhenCatch = (error, errorMessage) => {
      setStatus({ success: false });
      setErrors({ submit: errorMessage || error?.message || 'Try again later' });
      setSubmitting(false);
    };

    if (attachmentsBusy) {
      dispatch(addNotification('attachment-busy', ATTACHMENTS_BUSY_MESSAGE, NOTIFICATION_STATUS.INFO));
      setSubmitting(false);
      return;
    }

    // Handle Contact Card case - skip Azure upload since there's no file to upload
    // VCARD_FILENAME is a special marker that the backend recognizes
    let newValuesAttachment;
    if (values.attachment === VCARD_FILENAME) {
      // Contact Card selected - just use the VCARD_FILENAME directly
      newValuesAttachment = VCARD_FILENAME;
    } else {
      // 2022-12-08 - Eventually instead of passing "false" as the last parameter on this next call (allowDeleteFile), we'll want to call an endpoint on the server for most places that allow delete of an image (but not order edit custom message, parties, etc. that are "endpoints" themselves where we never copy that image to another place).  So, mainly templates & partyTypes as of now we need to check, but eventually campaigns that can be copied or shared, etc.
      newValuesAttachment = await helpers.azure.loadFileToAzureAndGetStatusCode(fileUpload, values.attachment, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, values.attachmentContainer, fileWasNotUpload, fileWasNotDelete, false, false, false);
      if (newValuesAttachment === null) {
        enqueueSnackbar('There are some problems with Azure service.', {
          variant: 'error'
        });
        return;
      }
    }
    if (helpers.isEmpty(values.systemGenMessage) && (!newValuesAttachment || helpers.isEmpty(newValuesAttachment))) {
      dispatch(addNotification('sendFile', 'You must enter either a text message or select an image.', NOTIFICATION_STATUS.ERROR));
      return;
    }
    const messageChanges = {
      id: helpers.transform.stringToNull(id),
      code: isItUserTemplate ? helpers.transform.toMessageCode(values.message, values.message2) : values.message,
      description: values.scenario,
      systemGenMessage: values.systemGenMessage,
      attachment: newValuesAttachment,
      attachmentContainer: values.attachmentContainer,
      // CON-1936 — files 2..N, sent only in the beta (absent otherwise, so
      // the server keeps whatever is stored).
      ...definitionExtrasPayload({
        user,
        mmsAttachmentPolicy,
        extrasField: 'additionalAttachments',
        extras: values.additionalAttachments,
      }),
    };
    dispatch(addNewTemplate && isItUserTemplate ? createMessage(messageChanges, doWhenTry, doWhenCatch) : updateMessage(messageChanges, doWhenTry, doWhenCatch));
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    values,
    setFieldValue,
    setFieldTouched
  } = formik;

  const handleFocusFieldValidate = (event) => {
    helpers.formValidation.validateField(event, setFieldTouched);
  };

  // CON-1936 — the multi-attachment beta: files 2..N beside the legacy pair.
  const definitionAttachments = useDefinitionAttachments({
    formik,
    legacyField: 'attachment',
    containerField: 'attachmentContainer',
    extrasField: 'additionalAttachments',
    user,
    azAccountName,
    databaseBeingUsed,
    setPendingUpload: (value) => dispatch(setFileUploadToMessage(value)),
  });

  useEffect(() => {
    setAttachmentsBusy(definitionAttachments.busy);
  }, [definitionAttachments.busy]);

  // CON-1621 — a template's usable context is a fact about its text: the
  // Smart Field token prefix encodes entity type. This mirrors the server's
  // deriveTemplateContextScope so the modal shows, live, the same answer the
  // save will persist. Prefix matching (not a full token parse) is
  // deliberate: pasted tokens may be in label format rather than key format.
  const messageBody = values.systemGenMessage || '';
  // CON-1621 follow-up — BOTH token systems count toward scope. A legacy
  // dynamicFields token like [Host First Name] is every bit as event-only
  // as [SFE:], but carries no prefix to detect it by, so it is matched
  // against the curated LEGACY_TOKEN_SCOPE list. Before this, the badge
  // read "Use anywhere" while the user typed [Host First Name], which is
  // the exact moment they most needed to be told otherwise.
  const legacyScopes = findLegacyTokenScopes(messageBody);
  const hasGroupSmartField = messageBody.includes('[SFG:');
  const hasEventSmartField = messageBody.includes('[SFE:');
  const hasGroupScope = hasGroupSmartField || !!legacyScopes.group;
  const hasEventScope = hasEventSmartField || !!legacyScopes.event;
  const hasMixedSmartFields = hasGroupScope && hasEventScope;

  // Most restrictive wins, matching deriveTemplateContextScope server-side.
  const liveContextScope = (() => {
    if (hasEventScope) return TEMPLATE_CONTEXT_SCOPE.EVENT;
    if (hasGroupScope) return TEMPLATE_CONTEXT_SCOPE.GROUP;
    return TEMPLATE_CONTEXT_SCOPE.CONTACT;
  })();
  const liveScopeConfig = getTemplateContextScopeConfig(liveContextScope);

  // Whichever entity type the body already commits to constrains what can be
  // inserted next — the backend rejects a body carrying both.
  // Gated on the COMBINED scope: a body already committed to events by a
  // legacy token must not then offer Group Smart Fields, or the user builds
  // a template the save will reject.
  const disabledEntityTypes = useMemo(() => {
    if (hasGroupScope) {
      return [{
        entityType: 'PartyGuest',
        reason: `This template already uses ${SF_ENTITY_LABELS.GroupMember} or a group field. Those only fill in when texting in a group. A template can use Group or Event fields, not both.`,
      }];
    }
    if (hasEventScope) {
      return [{
        entityType: 'GroupMember',
        reason: `This template already uses ${SF_ENTITY_LABELS.PartyGuest} or an event field. Those only fill in when texting event guests. A template can use Group or Event fields, not both.`,
      }];
    }
    return [];
  }, [hasGroupScope, hasEventScope]);

  // Handle attachment changes from AttachmentField
  const handleFileChange = (change) => {
    const {
      attachment: newAttachment,
      fileUpload: newFileUpload,
      isContactCard,
    } = change;
    if (definitionAttachments.handleFileChange(change)) return;
    if (isContactCard) {
      // Contact Card selected - set fileUpload to null (no file to upload)
      // and set attachment to VCARD_FILENAME so it's saved correctly
      dispatch(setFileUploadToMessage(null));
      setFieldValue('attachment', VCARD_FILENAME, false);
      setFieldValue('attachmentContainer', null, false);
    } else if (newAttachment === null && newFileUpload === null) {
      // Removal - clear both Redux and Formik state
      dispatch(setFileUploadToMessage(null));
      setFieldValue('attachment', null, false);
      setFieldValue('attachmentContainer', null, false);
    } else if (newFileUpload) {
      // Regular file attachment - store file in Redux for upload during save
      dispatch(setFileUploadToMessage(newFileUpload));
      setFieldValue('attachment', null, false);
      setFieldValue('attachmentContainer', null, false);
    }
  };

  return (
    <form
      id="updateMessage"
      className={classes.updateMessageForm}
      onSubmit={handleSubmit}
    >
      <Grid
        container
        spacing={defaultScreenSpacing}
      >
        <Grid xs={12}>
          <div className={classes.formGroupLabel}>
            {isItUserTemplate ? 'Template #' : 'Message #'}
          </div>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={4}
      >
        <Grid
          xs={isItUserTemplate ? 6 : 12}
        >
          <TextFieldFormik
            touchedValue={touched.message}
            errorValue={errors.message}
            name="message"
            handleChange={handleChange}
            handleBlur={handleBlur}
            onFocus={handleFocusFieldValidate}
            value={values.message}
            disabled
            disabledBox
            required
          />
        </Grid>
        {isItUserTemplate && (
          <Grid xs={isItUserTemplate ? 6 : 12}>
            <TextFieldFormik
              touchedValue={touched.message2}
              errorValue={errors.message2}
              name="message2"
              handleChange={handleChange}
              handleBlur={handleBlur}
              onFocus={handleFocusFieldValidate}
              value={values.message2}
              disabled={loading}
              fieldType="numberFormat"
              format="###"
              required
            />
          </Grid>
        )}
      </Grid>
      <Grid
        className="formGridGrope"
        container
        spacing={defaultScreenSpacing}
      >
        <Grid xs={12}>
          <div className={classes.formGroupLabel}>Scenario</div>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={8}
      >
        <Grid xs={12}>
          <TextFieldFormik
            touchedValue={touched.scenario}
            errorValue={errors.scenario}
            name="scenario"
            handleChange={handleChange}
            handleBlur={handleBlur}
            onFocus={handleFocusFieldValidate}
            value={values.scenario}
            disabled={!isItUserTemplate || loading}
            disabledBox={!isItUserTemplate}
            {...isItUserTemplate ? { required: true } : {}}
            rows={5}
            multiline
          />
        </Grid>
      </Grid>
      <TextFieldEmojiMaxLength
        title="Text Message To Send"
        classes={classes}
        loading={loading}
        valueName="systemGenMessage"
        currentValue={values.systemGenMessage}
        handleBlur={handleBlur}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        maxTextMessageLength={MAX_TEXT_MESSAGE_LENGTH}
        windowWidth={windowWidth}
        errorValue={errors.systemGenMessage}
        touchedValue={touched.systemGenMessage}
        defaultScreenSpacing={defaultScreenSpacing}
        screenName={isItUserTemplate ? 'Message Templates Edit' : 'Order Edit'}
        disableTestDeliverability
        // CON-1621 — Message Templates can carry Contact, Group Contact, or
        // Event Guest Smart Fields; the token prefix decides where the
        // template can be used. Follow-Up (msg/default) templates stay
        // Contact-only: their send path resolves [SFC:] and nothing else.
        showSmartFieldsTab
        entityType={isItUserTemplate
          ? ['Contact', 'GroupMember', 'PartyGuest']
          : 'Contact'}
        disabledEntityTypes={isItUserTemplate ? disabledEntityTypes : []}
        // CON-1621 — scope indicator sits in the "Text Message To Send"
        // label row, ahead of the emoji and kebab icons: above the field,
        // where the user looks before typing. Chip first (the summary),
        // then the sentence (the elaboration). The sentence is dropped for
        // contact scope, where the chip already says it (matching the list
        // column), and hidden below md so it can never crush the title on
        // a narrow/fullscreen dialog. The chip's tooltip carries the same
        // wording, so the explanation is still reachable on mobile (and is
        // how contact scope surfaces its wording at all).
        titleRightContent={isItUserTemplate ? (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mr: 2,
          }}
          >
            <TooltipMUI6 title={liveScopeConfig.explanation}>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 1,
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  cursor: 'default',
                  backgroundColor: liveScopeConfig.bg,
                  color: liveScopeConfig.color,
                }}
              >
                {liveScopeConfig.label}
              </Box>
            </TooltipMUI6>
            {liveContextScope !== TEMPLATE_CONTEXT_SCOPE.CONTACT && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: { xs: 'none', md: 'block' } }}
              >
                {liveScopeConfig.explanation}
              </Typography>
            )}
          </Box>
        ) : null}
      />
      {isItUserTemplate && hasMixedSmartFields && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            borderRadius: '4px',
            border: '1px solid #E65100',
            backgroundColor: '#FFF3E0',
          }}
        >
          <Typography variant="caption" sx={{ color: '#E65100' }}>
            This template uses both Group and Event Smart Fields. A template can
            use one or the other, not both. Please remove one before saving.
          </Typography>
        </Box>
      )}
      <Grid container spacing={defaultScreenSpacing}>
        <Grid xs={12}>
          <AttachmentField
            title="Attachment"
            userId={user?.id}
            azAccountName={azAccountName}
            databaseBeingUsed={databaseBeingUsed}
            attachment={values.attachment}
            attachmentContainer={values.attachmentContainer}
            fileUpload={fileUpload}
            onFileChange={handleFileChange}
            loading={loading}
            errorValue={errors.attachment}
            validateMessage={VALIDATE_MESSAGE}
            textingProvider={user?.textingProvider}
            showContactCardOption
            {...definitionAttachments.fieldProps}
          />
        </Grid>
      </Grid>
    </form>
  );
}

EditMessage.propTypes = {
  message: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  userMessageCodes: PropTypes.array.isRequired,
};

export default withStyles(EditMessage, editMessageStyle, { withTheme: true });
